import { Card, Tooltip, Typography } from "@mui/material";
import { FORMULA_FIELD_CONSTRAINTS } from "portal/utils/almanac";
import {
  MULTIPLIER_FORMULA_FIELD_KEY,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { NumberInput } from "common/components/NumberInput";
import { useTranslation } from "react-i18next";
import { WhenTrue } from "common/utils/types";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { FC, ReactNode } from "react";

type BasicShootTimeCardProps = {
  band: ShootTimeBand;
  children?: ReactNode;
} & WhenTrue<
  "canEdit",
  {
    onMultiplierChange: (value: number) => void;
    onValidityChange: (isValid: boolean) => void;
  }
>;

const {
  min: MIN,
  max: MAX,
  step: STEP,
} = FORMULA_FIELD_CONSTRAINTS[MULTIPLIER_FORMULA_FIELD_KEY];

export const BasicShootTimeCard: FC<BasicShootTimeCardProps> = ({
  band: { color, label, formula },
  canEdit,
  onMultiplierChange,
  onValidityChange,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      variant="outlined"
      className="flex flex-col gap-2 px-3 py-4 rounded-lg"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-3 h-3 rounded-sm shrink-0"
            style={{ backgroundColor: color }}
          />
          <Typography className="truncate">{label}</Typography>
        </div>
        <div className="flex items-center gap-2 shrink">
          <Typography
            component="div"
            variant="body2"
            color="text.secondary"
            className="flex items-center gap-1 min-w-0 shrink"
          >
            {t(
              `views.categoryTemplates.shootTime.advancedFields.${MULTIPLIER_FORMULA_FIELD_KEY}.label`
            )}
            <Tooltip
              arrow
              title={t(
                `views.categoryTemplates.shootTime.advancedFields.${MULTIPLIER_FORMULA_FIELD_KEY}.description`
              )}
            >
              <InfoOutlinedIcon fontSize="inherit" tabIndex={0} />
            </Tooltip>
          </Typography>
          {canEdit ? (
            <NumberInput
              value={formula[MULTIPLIER_FORMULA_FIELD_KEY]}
              min={MIN}
              max={MAX}
              step={STEP}
              required
              onChange={(val) => {
                if (val === undefined) {
                  return;
                }
                onMultiplierChange(val);
              }}
              onValidityChange={onValidityChange}
              textFieldProps={{
                className:
                  "w-28 shrink-0 [&_.MuiInputBase-input]:font-semibold [&_.MuiInputBase-input]:px-1 [&_.MuiInputBase-input]:text-center",
              }}
            />
          ) : (
            <Typography className="font-semibold shrink-0">
              {formula[MULTIPLIER_FORMULA_FIELD_KEY]}
            </Typography>
          )}
        </div>
      </div>
      {children}
    </Card>
  );
};
